import { Cross2Icon, EyeNoneIcon, EyeOpenIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { InputHTMLAttributes, useEffect, useState } from 'react'
import * as styles from './text-input.css.ts'

type TextInputProps = {
  /** Current value of the input */
  value?: string
  /** Callback fired when the input value changes */
  onChange?: (value: string | number) => void
  /** Placeholder text shown when input is empty */
  placeholder?: string
  /** Label text shown above the input */
  label?: string
  /** HTML id attribute. Required for accessibility when using a label */
  id?: string
  /** HTML name attribute */
  name?: string
  /** Shows "(Optional)" text next to label. Do not use together with isRequired */
  isOptional?: boolean
  /** Shows asterisk (*) next to label. Do not use together with isOptional */
  isRequired?: boolean
  /** Helper text shown below the input */
  helperText?: string
  /** Input type - controls input behavior and appearance */
  type?: 'text' | 'password' | 'search' | 'number'
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value' | 'type'>

export function TextInput({
  value,
  onChange,
  placeholder = '',
  label,
  id,
  name,
  isOptional,
  isRequired,
  helperText,
  type = 'text',
  ...props
}: TextInputProps) {
  const [showPassword, setShowPassword] = useState(false)
  const isPassword = type === 'password'
  const isSearch = type === 'search'

  return (
    <div className={styles.inputWrapper}>
      {label && (
        <div className={styles.labelContainer}>
          <label htmlFor={id} className={styles.inputLabel}>
            {label}
            {isRequired && ' *'}
          </label>
          {isOptional && <span className={styles.optionalText}>(Optional)</span>}
        </div>
      )}
      <DebouncedInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        id={id}
        name={name}
        type={isPassword ? (showPassword ? 'text' : 'password') : type}
        isPassword={isPassword}
        showPassword={showPassword}
        onTogglePassword={() => setShowPassword(!showPassword)}
        isSearch={isSearch}
        {...props}
      />
      {helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  )
}

type DebouncedInputProps = {
  /** Current value of the input */
  value?: string
  /** Callback fired when the input value changes, after debounce delay */
  onChange?: (value: string) => void
  /** Delay in milliseconds before onChange is fired after user stops typing */
  debounce?: number
  /** Placeholder text shown when input is empty */
  placeholder?: string
  /** HTML id attribute */
  id?: string
  /** HTML name attribute */
  name?: string
  /** Whether this is a password input with show/hide functionality */
  isPassword?: boolean
  /** Whether password is currently shown as plain text */
  showPassword?: boolean
  /** Callback fired when show/hide password button is clicked */
  onTogglePassword?: () => void
  /** Whether this is a search input with magnifying glass icon and clear button */
  isSearch?: boolean
  /** Whether the input is disabled */
  disabled?: boolean
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value'>

function DebouncedInput({
  value: initialValue = '',
  onChange = () => {},
  debounce = 200,
  placeholder,
  id,
  name,
  isPassword,
  showPassword,
  onTogglePassword,
  isSearch,
  disabled,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState<string>(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  const handleClear = () => {
    setValue('')
    onChange('')
  }

  return (
    <div className={styles.inputContainer} data-disabled={disabled}>
      {isSearch && <MagnifyingGlassIcon className={styles.searchIcon} width={16} height={16} />}
      <input
        className={styles.textInput({ hasLeftIcon: isSearch })}
        value={value}
        placeholder={placeholder}
        onChange={e => setValue(e.target.value)}
        id={id}
        name={name}
        disabled={disabled}
        {...props}
      />
      {isPassword && (
        <button
          type="button"
          onClick={onTogglePassword}
          className={styles.eyeButton}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          disabled={disabled}
        >
          {showPassword ? <EyeOpenIcon width={12} height={12} /> : <EyeNoneIcon width={12} height={12} />}
        </button>
      )}
      {isSearch && value && !disabled && (
        <button type="button" onClick={handleClear} className={styles.clearButton} aria-label="Clear search">
          <Cross2Icon width={12} height={12} />
        </button>
      )}
    </div>
  )
}
